import LabsUpdatesNotify from './LabsUpdatesNotify';
import { useEffect, useRef, useState } from 'react';
import { programNotification, type GetToken } from './previewNotifications';
import { previewDestinations } from './destinations';

export interface NotifyCopy {
  button: string;
  savedTitle: string;
  savedDescription: string;
  stop: string;
  off: string;
}

export const PREVIEW_UPDATES_COPY: NotifyCopy = {
  button: 'Get Labs updates',
  savedTitle: 'You’re on the list.',
  savedDescription: 'When something new lands in Labs, one email. Never more than one a month.',
  stop: 'Stop updates',
  off: 'Labs updates are off.',
};

/** Mounted per identity so an old account's result cannot appear for a new one. */
function ProgramNotify({ program, getToken, onClick, onSaved, copy }: {
  /** The study-app program the preference is stored against, and its name for the copy. */
  program: { id: string; name: string };
  copy?: NotifyCopy;
  getToken: GetToken;
  onClick: () => void;
  onSaved: (notify: boolean) => void;
}) {
  const [notified, setNotified] = useState<boolean | null>(null);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const request = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    request.current = controller;
    setFailed(false);
    programNotification(program.id, getToken, controller.signal)
      .then(value => { if (!controller.signal.aborted) setNotified(value); })
      .catch(() => { if (!controller.signal.aborted) setFailed(true); })
      .finally(() => { if (request.current === controller) request.current = null; });
    return () => { controller.abort(); request.current?.abort(); };
  }, [program.id, getToken, retry]);

  const save = async (value: boolean) => {
    if (request.current || notified === null) return;
    onClick();
    const controller = new AbortController();
    request.current = controller;
    setPending(true);
    setFailed(false);
    try {
      const saved = await programNotification(program.id, getToken, controller.signal, value);
      if (controller.signal.aborted) return;
      if (saved !== value) throw new Error('Preference was not saved.');
      setNotified(saved);
      onSaved(saved);
    } catch {
      if (!controller.signal.aborted) setFailed(true);
    } finally {
      if (!controller.signal.aborted) setPending(false);
      if (request.current === controller) request.current = null;
    }
  };

  return (
    <div className={notified === true ? 'preview-notification-saved' : undefined}>
      <div role="status" className={notified === true ? undefined : 'preview-sr-only'}>
        {notified === true ? (
          <div className="preview-notification-confirmation">
            {/* Lucide BellRing, ISC license: /licenses/lucide.txt */}
            <svg className="preview-notification-bell" aria-hidden="true" width="28" height="28"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.268 21a2 2 0 0 0 3.464 0" />
              <path d="M22 8c0-2.3-.8-4.3-2-6" />
              <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
              <path d="M4 2C2.8 3.7 2 5.7 2 8" />
            </svg>
            <div className="preview-notification-copy">
              <p className="preview-notification-title">{copy?.savedTitle ?? 'You’re on the list.'}</p>
              <p className="preview-notification-description">{copy?.savedDescription ?? 'We’ll email you when it is ready.'}</p>
            </div>
            <button className="preview-stop-notifications" type="button" disabled={pending} onClick={() => void save(false)}>
              {pending ? 'Saving…' : copy?.stop ?? 'Stop notifications'}
            </button>
          </div>
        ) : notified === false ? copy?.off ?? `Notifications for ${program.name} are off.` : null}
      </div>
      {notified !== true && (
        <button className="preview-notify" type="button" disabled={pending || (notified === null && !failed)}
          onClick={() => notified === null ? setRetry(value => value + 1) : void save(true)}>
          {pending ? 'Saving…' : notified === null ? failed ? 'Try again' : 'Loading…' : copy?.button ?? 'Notify me'}
        </button>
      )}
      {failed && <p role="alert" className="preview-unavailable">
        {notified === null ? 'Could not load your notification preference.' : 'Could not save your preference. Please try again.'}
        {notified === null && <>{' '}<a href={previewDestinations(window.location.hostname).studyPreview}>Check your study account</a>.</>}
      </p>}
    </div>
  );
}

export default function PreviewNotify(props: Parameters<typeof ProgramNotify>[0]) {
  return props.program.id === 'preview-updates'
    ? <LabsUpdatesNotify getToken={props.getToken} onClick={props.onClick} onSaved={props.onSaved} />
    : <ProgramNotify {...props} />;
}
